import { Component, TemplateRef, signal, effect, input, output, afterNextRender, DestroyRef, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroupDirective, NgForm, ReactiveFormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { debounceTime, firstValueFrom, Observable, startWith } from 'rxjs';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ErrorStateMatcher } from '@angular/material/core';

class ExternalValidationStateMatcher implements ErrorStateMatcher {
  constructor(private readonly getValidationControl: () => AbstractControl | null) {}

  isErrorState(_: FormControl | null, __: FormGroupDirective | NgForm | null): boolean {
    const validationControl = this.getValidationControl();
    return !!validationControl && validationControl.invalid && (validationControl.touched || validationControl.dirty);
  }
}

@Component({
  selector: 'adm-type-ahead-selector2',
  templateUrl: './type-ahead-selector2.component.html',
  styleUrl: './type-ahead-selector2.component.scss',
  imports: [
    ReactiveFormsModule,
    NgTemplateOutlet,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatTooltipModule,
    CustomDropDownArrowDirective,
  ],
})
export class TypeAheadSelector2Component<T> {
  label = input.required<string>();
  required = input<boolean>(false);
  placeholder = input<string>('Søg...');
  fetchFn = input.required<(term: string) => Observable<T[]>>(); //Function called to retrieve items whenever search input changes
  fetchByIdFn = input<((id: string) => Observable<T | null>) | undefined>(); //Function to fetch a specific item. Used with initialId value
  initialId = input<string | undefined>(); //Allows loading of a specific initial value. Used with fetchByIdFn
  itemTemplate = input<TemplateRef<any>>(); //Template allowing to format items in the dropdown
  displayPropertyName = input<string>('name'); //Value to display for selected item in the input element
  identityPropertyName = input.required<string>(); //Used to track state of result items in the loop in the template
  resetTrigger = input<{ reset: boolean }>(); //A way for a parent component to trigger a reset
  clearTypeAhead = output();
  itemSelected = output<T | null>(); //Event emitted when an item is selected
  disabled = input<boolean>(false);
  suffixIcon = input<string | null>(null);
  suffixIconTooltip = input<string | null>(null);
  validationControl = input<AbstractControl | null>(null);
  requiredErrorText = input<string>('Feltet er påkrævet');

  public displayFn = (item: T | string): string => {
    if (typeof item === 'string') {
      return item;
    }

    const displayValue = (item as Record<string, unknown> | null)?.[this.displayPropertyName()];
    return displayValue != null ? String(displayValue) : '';
  };

  searchControl = new FormControl<string | T>('');
  searchTerm = toSignal(this.searchControl.valueChanges.pipe(startWith(''), debounceTime(300), takeUntilDestroyed()));
  selectedItem = signal<T | null>(null);
  results = signal<T[]>([]);
  loading = signal(false);
  readonly externalValidationStateMatcher: ErrorStateMatcher = new ExternalValidationStateMatcher(() => this.validationControl());
  //we need to prevent inital emission of OnSelect, signal does not have such option built in
  private isInitialized = signal(false);
  private previousSearchTerm: string | T | null | undefined;
  //guards the async initialId fetch below from emitting after the component is destroyed
  private isDestroyed = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.isDestroyed = true;
    });
    afterNextRender(() => {
      this.isInitialized.set(true);
    });
    effect(() => {
      if (this.disabled()) {
        this.searchControl.disable();
      } else {
        this.searchControl.enable();
      }
    });
    //Reset trigger
    effect(() => {
      const resetTrigger = this.resetTrigger();
      if (resetTrigger?.reset) {
        this.clearSelection();
      }
    });

    //Trigger a search when the input changes
    effect(async () => {
      const term = this.searchTerm();
      // comparing previous and current term value for empty
      // is in case nothing was selected and we want
      // to reset filter with fresh values
      if (term === this.previousSearchTerm && term != '' && this.previousSearchTerm != '') return;
      this.previousSearchTerm = term;
      const fetchFunction = this.fetchFn();
      this.loading.set(true);
      if (typeof term !== 'object') {
        const termString = typeof term === 'string' ? term : '';
        try {
          this.results.set(await firstValueFrom(fetchFunction(termString)));
        } catch {
          this.results.set([]);
        }
      }
      this.loading.set(false);
    });

    //Trigger initial fetch if an initial ID is provided
    effect(async () => {
      const initialIdValue = this.initialId();
      const fetchByIdFunction = this.fetchByIdFn();
      if (initialIdValue && fetchByIdFunction) {
        try {
          const fetchedItem = await firstValueFrom(fetchByIdFunction(initialIdValue));
          if (this.isDestroyed) return;
          this.searchControl.setValue(fetchedItem, { emitEvent: false });
          this.onSelect(fetchedItem);
        } catch {
          if (this.isDestroyed) return;
          this.searchControl.setValue('', { emitEvent: false });
          this.onSelect(null);
        }
      }
    });
  }

  onSelect(item: T | null) {
    if (!this.isInitialized()) {
      this.selectedItem.set(item);
      return;
    }
    this.itemSelected.emit(item);
    this.selectedItem.set(item);
  }

  clearInput() {
    this.searchControl.setValue('');
    this.results.set([]);
  }

  clearSelection() {
    this.onSelect(null);
    this.searchControl.setValue('');
    this.clearTypeAhead.emit();
  }

  markValidationControlAsTouched(): void {
    const control = this.validationControl();
    if (!control) {
      return;
    }

    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
  }

  hasRequiredValidationError(): boolean {
    const control = this.validationControl();
    return !!control && control.hasError('required') && (control.touched || control.dirty);
  }

  trackByIdentity(option: T): unknown {
    return (option as Record<string, unknown>)[this.identityPropertyName()];
  }

  async refreshResults(selectId?: string) {
    this.previousSearchTerm = undefined;
    this.loading.set(true);
    try {
      const results = await firstValueFrom(this.fetchFn()(''));
      this.results.set(results);
      if (selectId) {
        const item = results.find(r => String((r as Record<string, unknown>)[this.identityPropertyName()]) === selectId);
        if (item) {
          this.searchControl.setValue(item, { emitEvent: false });
          this.selectedItem.set(item);
        }
      }
    } catch {
      this.results.set([]);
    }
    this.loading.set(false);
  }
}
